'use client'

import { Container, ContainerProps, Item } from './styles'

export interface TextTabsProps<
  T extends string | number | Record<string, unknown>,
> extends ContainerProps {
  /**
   * Class name
   */
  className?: string

  /**
   * Tabs
   */
  tabs: {
    label: string
    value: T
  }[]

  /**
   * Active tab
   */
  activeTabValue: T

  /**
   * onChange handler
   */
  onChange: (tabValue: T) => void
}

// Check if two objects are deeply equal
const deepEqual = (a: Record<string, unknown>, b: Record<string, unknown>) => {
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)

  if (keysA.length !== keysB.length) return false

  for (const key of keysA) {
    if (typeof a[key] === 'object' && typeof b[key] === 'object') {
      if (
        !deepEqual(
          a[key] as Record<string, unknown>,
          b[key] as Record<string, unknown>,
        )
      )
        return false
    } else {
      if (a[key] !== b[key]) return false
    }
  }

  return true
}

/**
 * TextTabs component
 */
export const TextTabs = <T extends string | number | Record<string, unknown>>({
  tabs,
  activeTabValue,
  onChange,
  ...rest
}: TextTabsProps<T>) => {
  return (
    <Container {...rest}>
      {tabs.map(({ label, value }) => {
        const active =
          typeof activeTabValue === 'object' && typeof value === 'object'
            ? deepEqual(activeTabValue, value)
            : activeTabValue === value

        return (
          <Item
            key={label}
            active={active}
            disabled={active}
            onClick={() => onChange(value)}
          >
            {label}
          </Item>
        )
      })}
    </Container>
  )
}
